import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotificationsOptional } from '../../context/NotificationContext';
import { JOBS_PORTAL_PATH } from '../../config/paths';
import {
    formatNotificationTime,
    getNotificationIcon,
    getFirstUnreadTaskNotificationLink,
    getNotificationLink,
} from '../../utils/notificationMeta';
import CountBadge from './CountBadge';

const NotificationBell = ({ className = '' }) => {
    const ctx = useNotificationsOptional();
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onEscape = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onEscape);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onEscape);
        };
    }, [open]);

    if (!ctx) return null;

    const {
        notifications,
        unreadCount,
        menuBadges,
        loading,
        markRead,
        markAllRead,
        refresh,
    } = ctx;

    const recent = notifications.slice(0, 12);
    const hasTaskAlerts = menuBadges.tasks > 0;
    const hasDisputeAlerts = menuBadges.disputes > 0;
    const taskQuickLink = getFirstUnreadTaskNotificationLink(notifications);
    const unreadDisputeNotification = notifications.find(
        (n) =>
            !n.readStatus &&
            n.taskId &&
            String(n.type || '').toLowerCase().includes('dispute'),
    );
    const disputeQuickLink = unreadDisputeNotification
        ? getNotificationLink(unreadDisputeNotification)
        : '/dispute';

    return (
        <div ref={panelRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:text-white"
                aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
                aria-expanded={open}
            >
                <i className="fa-regular fa-bell text-base" />
                <CountBadge value={unreadCount} position="absolute" tone="rose" />
            </button>

            {open ? (
                <div className="absolute right-0 top-[calc(100%+8px)] z-[100] w-[min(100vw-1.5rem,380px)] overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-950">
                    <div className="flex items-center justify-between gap-2 border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
                        <div>
                            <p className="text-sm font-bold text-zinc-900 dark:text-white">
                                Notifications
                            </p>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-500">
                                {unreadCount
                                    ? `${unreadCount} unread`
                                    : 'You are all caught up'}
                            </p>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => refresh()}
                                className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                                title="Refresh"
                            >
                                <i
                                    className={`fa-solid fa-rotate-right text-xs ${loading ? 'animate-spin' : ''}`}
                                />
                            </button>
                            {unreadCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => markAllRead()}
                                    className="rounded-lg px-2 py-1.5 text-[11px] font-semibold text-teal-800 transition-colors hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-950/50"
                                >
                                    Mark all read
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {(hasTaskAlerts || hasDisputeAlerts) && (
                        <div className="space-y-2 border-b border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/40">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                                Quick actions
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {hasTaskAlerts ? (
                                    <Link
                                        to={taskQuickLink}
                                        onClick={() => setOpen(false)}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-[11px] font-semibold text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-200"
                                    >
                                        <i className="fa-solid fa-briefcase text-[10px]" />
                                        Open task
                                        <CountBadge
                                            value={menuBadges.tasks}
                                            tone="teal"
                                            className="!ml-0 !h-4 !min-w-[16px] !px-1 !text-[9px]"
                                        />
                                    </Link>
                                ) : null}
                                {hasDisputeAlerts ? (
                                    <Link
                                        to={disputeQuickLink}
                                        onClick={() => setOpen(false)}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-semibold text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200"
                                    >
                                        <i className="fa-solid fa-flag text-[10px]" />
                                        Disputes
                                        <CountBadge
                                            value={menuBadges.disputes}
                                            tone="rose"
                                            className="!ml-0 !h-4 !min-w-[16px] !px-1 !text-[9px]"
                                        />
                                    </Link>
                                ) : null}
                            </div>
                        </div>
                    )}

                    <ul className="max-h-[min(60vh,420px)] overflow-y-auto">
                        {recent.length === 0 ? (
                            <li className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-500">
                                No notifications yet.
                            </li>
                        ) : (
                            recent.map((notification) => {
                                const unread = !notification.readStatus;
                                const href = getNotificationLink(notification);
                                return (
                                    <li
                                        key={notification.id}
                                        className={`border-b border-zinc-100 last:border-b-0 dark:border-zinc-800/80 ${
                                            unread
                                                ? 'bg-teal-50/40 dark:bg-teal-950/20'
                                                : ''
                                        }`}
                                    >
                                        <Link
                                            to={href}
                                            onClick={() => {
                                                if (unread) markRead(notification.id);
                                                setOpen(false);
                                            }}
                                            className="flex gap-3 px-4 py-3 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                                        >
                                            <span
                                                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                                                    unread
                                                        ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300'
                                                        : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                                                }`}
                                            >
                                                <i
                                                    className={`${getNotificationIcon(notification.type)} text-xs`}
                                                />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="flex items-start justify-between gap-2">
                                                    <span className="text-sm font-semibold text-zinc-900 dark:text-white">
                                                        {notification.title}
                                                    </span>
                                                    {unread ? (
                                                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-teal-500" />
                                                    ) : null}
                                                </span>
                                                <span className="mt-0.5 line-clamp-2 text-xs text-zinc-600 dark:text-zinc-400">
                                                    {notification.body}
                                                </span>
                                                <span className="mt-1 block text-[10px] text-zinc-400">
                                                    {formatNotificationTime(
                                                        notification.createdAt,
                                                    )}
                                                </span>
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })
                        )}
                    </ul>

                    {notifications.length > recent.length ? (
                        <div className="border-t border-zinc-200 px-4 py-2 text-center dark:border-zinc-800">
                            <Link
                                to={`${JOBS_PORTAL_PATH}?section=workspace`}
                                onClick={() => setOpen(false)}
                                className="text-xs font-semibold text-teal-800 dark:text-teal-400"
                            >
                                View workspace activity
                            </Link>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default NotificationBell;
